import { useMemo, useCallback } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { Bar } from "react-chartjs-2";
import { ChartOptions, Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip } from "chart.js";

import { ReactComponent as CheckCircle } from "assets/images/iconsV2/check-circle.svg";
import useGetBookedGraphData from "api/queries/user/useGetBookedGraphData";
import Card from "components/v2/Card/Card";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import "./BookedGraphWidget.scss";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

function BookedGraphWidget({ isProvider }: { isProvider: boolean }) {
  const intl = useIntl();
  const { data: bookedGraphData, isFetching } = useGetBookedGraphData();

  const fillMissingMonths = useCallback(
    (data: { month_number: number; count: number }[]) => {
      const currentMonth = new Date().getMonth() + 1;
      const startMonth = currentMonth <= 6 ? 1 : currentMonth - 5;
      const dataMap = new Map(data.map((item) => [item.month_number, item.count]));

      return Array.from({ length: 6 }, (_, i) => ({
        month: new Date(2024, startMonth + i - 1).toLocaleString(intl.locale, { month: "short" }),
        value: dataMap.get(startMonth + i) || 0,
      }));
    },
    [intl.locale],
  );

  const chartData = useMemo(() => {
    if (!bookedGraphData) return [];
    return fillMissingMonths(bookedGraphData.chart_data);
  }, [bookedGraphData]);

  const stats = useMemo(
    () => [
      {
        label: intl.formatMessage({
          id: "dashboard.bookedGraphWidget.yearToDate",
          defaultMessage: "Year to date",
        }),
        value: bookedGraphData?.year_to_date_shipments,
        align: "start",
      },
      {
        label: intl.formatMessage({
          id: "dashboard.bookedGraphWidget.monthToDate",
          defaultMessage: "Month to date",
        }),
        value: bookedGraphData?.month_to_date_shipments,
        align: "end",
      },
    ],
    [intl, bookedGraphData],
  );

  // Calculate max tick and stepsize
  const getYAxisLimits = (data: { value: number }[]) => {
    if (data.length === 0) return { maxTick: 100, stepSize: 20 };

    const maxValue = Math.max(...data.map((d) => d.value));
    const maxTick = Math.ceil(maxValue / 10) * 10;
    return { maxTick, stepSize: Math.ceil(maxTick / 5) };
  };

  const { maxTick, stepSize } = useMemo(() => getYAxisLimits(chartData), [chartData]);

  const chartDataset = useMemo(
    () => ({
      labels: chartData.map((d) => d.month),
      datasets: [
        {
          label: intl.formatMessage({
            id: "dashboard.bookedGraphWidget.title",
            defaultMessage: "Booked shipments",
          }),
          data: chartData.map((d) => (d.value === 0 ? null : d.value)),
          backgroundColor: "#0071CE",
          hoverBackgroundColor: "#004B89",
          borderRadius: 6,
          barThickness: 32,
        },
      ],
    }),
    [chartData, intl],
  );

  const options: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label(context: any) {
            return ` ${context.raw} `;
          },
        },
        caretPadding: 5,
        backgroundColor: "hsl(229, 84.00%, 4.90%)",
        titleSpacing: 0,
        titleFont: {
          size: 0,
        },
        bodyFont: {
          weight: "bold",
          size: 12,
        },
        displayColors: false,
        yAlign: "bottom",
        cornerRadius: 12,
        padding: {
          top: 4,
          bottom: 8,
          left: 12,
          right: 12,
        },
      },
    },
    scales: {
      y: {
        type: "linear",
        beginAtZero: true,
        max: maxTick,
        ticks: {
          stepSize,
        },
        grid: {
          color: "#F1F5F9",
          drawBorder: false,
        },
      },
      x: {
        type: "category",
        grid: {
          display: false,
          drawBorder: false,
        },
      },
    },
  };

  return (
    <Card borderRadius="md" shadow="xs" style={{ height: "100%" }}>
      <div className="booked-graph-widget" style={{ height: isProvider ? "400px" : "324px" }}>
        <div className="booked-graph-widget__header">
          <div className="booked-graph-widget__icon">
            <CheckCircle />
          </div>
          <div className="w-100 d-flex align-items-center">
            <div className="font-medium text-md text-primary mr-2">
              <FormattedMessage id="dashboard.bookedGraphWidget.title" defaultMessage="Total shipments" />
            </div>
          </div>
        </div>
        {isFetching ? (
          <LoadingSkeleton rows={2} />
        ) : (
          <div className="booked-graph-widget__content">
            <div className="booked-graph-widget__info">
              {stats.map(({ label, value, align }) => (
                <div key={label} className={`booked-graph-widget__info__item align-items-${align}`}>
                  <div className="text-xs font-medium text-secondary-v2">{label}</div>
                  <div className="display-xs font-semibold text-primary">{value}</div>
                </div>
              ))}
            </div>
            <div className="booked-graph-widget__graph" style={{ height: isProvider ? "250px" : "175px" }}>
              <Bar data={chartDataset} options={options as ChartOptions<"bar">} />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

export default BookedGraphWidget;
